import {
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';

import {
  threeDSecure as braintreeThreeDSecure,
  Client,
  ThreeDSecure,
  ThreeDSecureVerifyOptions,
  ThreeDSecureVerifyPayload,
  GooglePaymentTokenizePayload,
} from 'braintree-web';

import { useAppSelector } from '@/store/hooks';
import { logger } from '@/services/logger/logger';
import { ProvidersLibsContext } from '@/services/providersLibs/providersLibsContext';
import { PaymentSelectState } from '@/features/paymentSelect/paymentSelectSlice';

import { separatePaymentDataName } from './googlepayUtils';

const initThreeDSecure = async (client: Client) => (
  braintreeThreeDSecure.create({
    client,
    version: 2,
  })
);

const buildVerifyCardPayload = (
  paymentSelectState: PaymentSelectState,
  paymentData: google.payments.api.PaymentData,
  tokenizeResult: GooglePaymentTokenizePayload,
): ThreeDSecureVerifyOptions => ({
  onLookupComplete: (_, next) => { next(); },
  nonce: tokenizeResult.nonce,
  amount: paymentSelectState.amount.toString(),
  bin: tokenizeResult.details.bin,
  collectDeviceData: true,
  email: paymentData?.email ?? '',
  billingAddress: {
    givenName: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').firstName,
    surname: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').lastName,
    streetAddress: paymentData?.shippingAddress?.address1 ?? '',
    extendedAddress: paymentData?.shippingAddress?.address2 ?? '',
    line3: paymentData?.shippingAddress?.address3 ?? '',
    locality: paymentData?.shippingAddress?.locality ?? '',
    region: '',
    postalCode: paymentData?.shippingAddress?.postalCode ?? '',
    countryCodeAlpha2: paymentData?.shippingAddress?.countryCode ?? '',
  },
  additionalInformation: {
    shippingGivenName: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').firstName,
    shippingSurname: separatePaymentDataName(paymentData?.shippingAddress?.name ?? '').lastName,
    shippingAddress: {
      streetAddress: paymentData?.shippingAddress?.address1 ?? '',
      extendedAddress: paymentData?.shippingAddress?.address2 ?? '',
      line3: paymentData?.shippingAddress?.address3 ?? '',
      locality: paymentData?.shippingAddress?.locality ?? '',
      region: '',
      postalCode: paymentData?.shippingAddress?.postalCode ?? '',
      countryCodeAlpha2: paymentData?.shippingAddress?.countryCode ?? '',
    },
  },
});

export function useBraintreePayment(
  initCallbacks?: {
    onSuccess?: () => void,
    onError?: (error?: unknown) => void,
  }
) {
  const onSuccess = useRef<(() => void) | undefined>(undefined);
  const onError = useRef<((error?: unknown) => void) | undefined>(undefined);
  onSuccess.current = initCallbacks?.onSuccess;
  onError.current = initCallbacks?.onError;

  const paymentSelectState = useAppSelector(state => state.paymentSelect);

  const providersLibs = useContext(ProvidersLibsContext);

  const [threeDSecure, setThreeDSecure] = useState<ThreeDSecure>();

  // Load Braintree threeDSecure instance on component load
  useEffect(() => {
    async function loadBraintree() {
      // Wait until Braintree client is available before loading threeDSecure
      if (!providersLibs?.braintreeClient) {
        return;
      }

      if (!threeDSecure) {
        try {
          const threeDSecureInstance = await initThreeDSecure(providersLibs.braintreeClient);
          setThreeDSecure(threeDSecureInstance);
        } catch (error) {
          logger.error('Error initialising Braintree threeDSecure');
          logger.error(error);
          if (onError.current) {
            onError.current(error);
          }
          return;
        }
      }

      if (onSuccess.current) {
        onSuccess.current();
      }
    }
    void loadBraintree();
  }, [
    providersLibs?.braintreeClient,
    threeDSecure,
    initCallbacks,
  ]);

  const triggerPayment = useCallback(async (
    paymentData: google.payments.api.PaymentData,
    tokenizeResult: GooglePaymentTokenizePayload,
    callbacks?: {
      onSuccess?: (threeDSecureResult: ThreeDSecureVerifyPayload | null, tokenizeResult: GooglePaymentTokenizePayload) => void,
      onError?: (error?: unknown) => void,
    }
  ): Promise<ThreeDSecureVerifyPayload | GooglePaymentTokenizePayload | null> => {
    if (!threeDSecure) {
      logger.error('Error initialising Braintree threeDSecure');
      if (callbacks?.onError) {
        callbacks.onError(new Error('Error initialising Braintree threeDSecure'));
      }
      return null;
    }

    // For tokenised cards (i.e. CRYPTOGRAM_3DS), 3DS check should be skipped (as per Braintree/GooglePay docs)
    if (tokenizeResult.details.isNetworkTokenized) {
      if (callbacks?.onSuccess) {
        callbacks.onSuccess(null, tokenizeResult);
      }

      return tokenizeResult;
    }

    // API Reference: https://braintree.github.io/braintree-web/current/ThreeDSecure.html#verifyCard
    // Example: https://developer.paypal.com/braintree/docs/guides/3d-secure/client-side/javascript/v3/#hosted-fields
    let threeDSecureResult;
    try {
      const payload = buildVerifyCardPayload(paymentSelectState, paymentData, tokenizeResult);
      threeDSecureResult = await threeDSecure.verifyCard(payload);
    } catch (error) {
      logger.error('Braintree 3DS/SCA error verifying card');
      logger.error(error);
      if (callbacks?.onError) {
        callbacks.onError(new Error('Braintree 3DS/SCA error verifying card'));
      }
      return null;
    }

    if (callbacks?.onSuccess) {
      callbacks.onSuccess(threeDSecureResult, tokenizeResult);
    }

    return threeDSecureResult;
  }, [threeDSecure, paymentSelectState]);

  return {
    triggerPayment,
  };
}
